<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="600px"
  >
    <template v-slot:activator="{ on }">
      <v-btn
        color="success"
        icon
        v-on="on"
      >
        <v-icon>mdi-plus</v-icon>
      </v-btn>
    </template>

    <material-card
      :offset="10"
      :full-width="true"
      color="green"
      title="Task"
      text="Create Task on Behalf"
      class="mb-0"
    >
      <v-container v-if="dialog">
        <v-row no-gutters>  
          <v-col cols="12">
            <v-textarea
              v-model="content"
              v-validate="'required'"
              outlined
              label="Task Content"
              :rows="4"
              auto-grow
              class="mt-4"
              data-vv-name="task content"
              :error-messages="errors.collect('task content')"
            />
          </v-col>
          <v-col
            cols="12"
            class="pb-2"
          >
            <v-autocomplete
              v-model="priority"
              v-validate="'required'"
              :error-messages="errors.collect('priority')"
              data-vv-name="priority"
              label="Priority"
              item-value="id"
              item-text="name"
              :items="priorities"
              hide-no-data
              hide-selected
            />
          </v-col>
          <v-col
            cols="12"
            class="pb-2"
          >
            <v-autocomplete
              v-model="category"
              v-validate="'required'"
              :error-messages="errors.collect('category')"
              data-vv-name="category"
              label="Category"
              item-value="id"
              item-text="name"
              :items="taskCategories"
              hide-no-data
              hide-selected
            />
          </v-col>

          <v-col
            cols="12"
            class="pb-2"
          >
            <v-autocomplete
              v-model="selectedOwner"
              v-validate="'required'"
              :error-messages="errors.collect('owner')"
              data-vv-name="owner"
              label="Task Owner"
              item-value="employee_uid"
              item-text="user.name"
              :items="filteredOwners"
              :loading="ownerLoading"
              :search-input.sync="searchOwner"
              hide-no-data
              hide-selected
            />
          </v-col>
       
          <v-col
            cols="12"
            class="pb-2"
          >
            <v-autocomplete
              v-model="selectedViewer"
              :error-messages="errors.collect('viewer')"
              data-vv-name="viewer"
              label="Viewers"
              item-value="employee_uid"
              item-text="user.name"
              :items="filteredObservers"
              :loading="viewerLoading"
              :search-input.sync="searchViewer"
              multiple
              chips
              clearable
              hide-no-data
              hide-selected
              deletable-chips
            />
          </v-col>
          <v-col cols="12">
            <helper-file-input
              v-model="file" 
              :max-size-in-mb="2"
              :max-number="5"
              :multiple="true"
              data-vv-name="files"
              field-name="files"
              accept-type=".xlsx,.pdf,.docx,.jpeg,.jpg,.png"    
              extension="ext:xlsx,pdf,docx,jpeg,jpg,png"      
            >
              <div 
                slot="activator"
                class="mb-2"
              >   
                <v-btn                    
                  :disabled="progressing"
                  class="mx-2"    
                >
                  Add file
                </v-btn>
                <span> 
                  {{ file ? '' : 'No file chosen (.pdf, .docx, .xslsx, .jpeg, .jpg, .png only)' }}
                </span>
                <div
                  v-if="file"
                  class="mt-4"
                >
                  <p
                    v-for="selectedFile in file.selectedFiles"
                    :key="selectedFile.name"
                    class="mb-0"
                  >
                    <span class="font-weight-bold">{{ selectedFile.name }}</span>
                  </p>
                </div>
              </div>
            </helper-file-input>
            <span class="red--text text--lighten-1">{{ errors.first('files') }}</span>
          </v-col>
          <v-col cols="12">
            <v-menu
              v-model="date_menu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="290px"
              max-width="290px"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  v-model="due_date"
                  v-validate="'required'"
                  label="Due date"
                  prepend-icon="event"
                  :error-messages="errors.collect('due_date')"
                  data-vv-name="due_date"
                  data-vv-as="due date"
                  v-on="on"
                />
              </template>
              <v-date-picker
                v-model="due_date"
                no-title
                @input="date_menu = false"
              />
            </v-menu>
          </v-col>
        </v-row>
      </v-container>

      <v-card-actions>  
        <v-spacer />
        <v-btn
          :disabled="progressing"
          text
          @click="dialog = false"
        >
          Close
        </v-btn>
        <v-btn
          :loading="progressing"
          color="success"          
          @click="submitForm"
        >
          Create
        </v-btn>
      </v-card-actions>
    </material-card>
  </v-dialog>
</template>

<script>
  export default {
    components: {
      HelperFileInput: () => import('@/components/helper/FileInput'),
    },

    inject: ['$validator'],
    props: {
      employeeUid: {
        type: String,
        default: null,
      },
    },
    data () {
      return {
        dialog: false,
        progressing: false,
        viewerLoading: false,
        searchViewer: null,
        ownerLoading: false,
        searchOwner: null,
        content: null,
        due_date: null,
        date_menu: false,
        selectedViewer: null,
        priority: null,
        category: null,
        file: null,
        priorities: [],
        taskCategories: [],
        viewers: [],        
        selectedOwner: null,
        owners: [],
      }
    },

    computed: {
      filteredObservers () {
        if (!this.selectedOwner) return this.viewers

        return this.viewers.filter(viewer => {
          return viewer.employee_uid !== this.selectedOwner
        })
      },

      filteredOwners () {
        if (!this.selectedViewer) return this.owners

        return this.owners.filter(owner => {
          return !this.selectedViewer.includes(owner.employee_uid)
        })
      },
    },

    watch: {
      dialog (value) {
        if (value) {
          this.loadData()
        }
        this.resetForm()
      },

      searchViewer (val) {
        this.viewers.map(viewer => {
          if (viewer.user.name === val) return true
        })

        this.viewerLoading = true
        this.fetchViewerEntriesDebounced(val)
      },

      searchOwner (val) {
        this.owners.map(owner => {
          if (owner.user.name === val) return true
        })
        this.ownerLoading = true
        this.fetchOwnerEntriesDebounced(val)
      },
    },

    methods: {
      submitForm () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) return

            this.progressing = true
        
            this.$api.assigneeTasks().create(this.prepareFormData())
              .then(res => {
                if (res.status === 201) {
                  this.$emit('created')             
                  this.$snack({ message: 'Task Created' })
                  this.dialog = false    
                }
              })
              .catch(() => {
                this.$snack({ 
                  color: 'error',
                  message: 'Failed to create task', 
                })
              }).then(() => {
                this.progressing = false
              })
          })
      },

      loadData () {
        Promise.all([
          this.$api.subordinates().recursiveList(this.employeeUid),
          this.$api.taskPriorities().list(),
          this.$api.taskCategories().list(),
          this.$api.superiors().list(this.employeeUid, ''),
        ])
          .then(([employee, priority, taskCategory, owner]) => {
            this.viewers = _.orderBy(employee.data, ['user.name'], ['asc'])
            this.owners = _.orderBy(owner.data, ['user.name'], ['asc'])

            this.priorities = priority.data
            this.taskCategories = taskCategory.data
          })
      },

      prepareFormData () {
        if (this.file) {
          this.file.formData.set('owner_uid', this.selectedOwner)
          this.file.formData.set('viewer_uids', JSON.stringify(this.selectedViewer))
          this.file.formData.set('content', this.content)
          this.file.formData.set('due_date', this.due_date)
          this.file.formData.set('priority_id', this.priority)
          this.file.formData.set('task_category_id', this.category)

          return this.file.formData
        }

        let formData = new FormData()

        formData.append('owner_uid', this.selectedOwner)
        formData.append('viewer_uids', JSON.stringify(this.selectedViewer))
        formData.append('content', this.content)
        formData.append('due_date', this.due_date)
        formData.append('priority_id', this.priority)
        formData.append('task_category_id', this.category)

        return formData
      },

      searchEmployee (val, viewer) {
        this.$api.employees().search({ name: val })
          .then(response => {
            this.ownerLoading = false
            this.viewerLoading = false
            response.data.map(data => { 
              if (viewer) {
                this.viewers.push({ employee_uid: data.employee.employee_uid, user: { name: data.name } })
              } else {
                this.owners.push({ employee_uid: data.employee.employee_uid, user: { name: data.name } })
              }
            })
          })
      },

      fetchOwnerEntriesDebounced (val) {
        clearTimeout(this._searchTimerId)
        this._searchTimerId = setTimeout(() => {
          this.searchEmployee(val, false)
        }, 500) /* 500ms throttle */
      },

      fetchViewerEntriesDebounced (val) {
        clearTimeout(this._searchTimerId)
        this._searchTimerId = setTimeout(() => {
          this.searchEmployee(val, true)
        }, 500) /* 500ms throttle */
      },
      
      resetForm () {
        this.$validator.reset()
        this.selectedAssignee = null
        this.selectedViewer = null
        this.selectedOwner = null
        this.content = null
        this.file = null
        this.due_date = null
        this.priority = null
        this.category = null
      },
    },
  }
</script>
